import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BaseSelect from "components/Shared/Base/BaseSelect";
import ExportUserCODReportExcel from "./ExportUserCODReportExcel";
import { useLazyGetUserPendingPaymentReportDetailQuery } from "store/services/reportApi";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  {
    title: "Pending Payments Reports",
    path: "/app/crm/reports/user-delivery-report",
  },
  { title: "Pending Payment Report Detail" },
];

const getStatusStyle = (status) => {
  const styles = {
    "Not Paid": { backgroundColor: "#F8C471", textColor: "#D35400" },
    Paid: { backgroundColor: "#D4E6F1", textColor: "#2E86C1" },
    Collected: { backgroundColor: "#D5F4E6", textColor: "#229954" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const DeliveryUserReportDetail = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();

  const chargeTypeFromUrl = searchParams.get("chargeType");

  const [chargeType, setChargeType] = useState(chargeTypeFromUrl || "all");
  const [shipmentActive, setShipmentActive] = useState("true");

  const [paymentStatus, setPaymentStatus] = useState(
    searchParams.get("paymentStatus") || "Not Paid"
  );

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [
    fetchPendingUserReportDetail,
    { data: detailRes, isLoading, isFetching },
  ] = useLazyGetUserPendingPaymentReportDetailQuery();

useEffect(() => {
  if (chargeTypeFromUrl && chargeTypeFromUrl !== chargeType) {
    setChargeType(chargeTypeFromUrl);
  }
}, [chargeTypeFromUrl, chargeType]);

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => (
        <Box>
          <Link
            to={`/app/crm/shipment/${row?._id}/detail?tab=pricing`}
            className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <Typography variant="body2">{row?.shipmentId || "N/A"}</Typography>
          </Link>
          {row?.paymentLocation && (
            <Typography variant="caption" color="text.secondary">
              {row.paymentLocation}
            </Typography>
          )}
        </Box>
      ),
      grow: 1.5,
    },
    {
      name: "Type",
      selector: (row) => row?.chargeType,
      cell: (row) => {
        const typeColors = {
          Charge: { bg: "#FFF3E0", color: "#E65100" },
          "Cash on Delivery": { bg: "#E8F5E9", color: "#2E7D32" },
          Discount: { bg: "#E3F2FD", color: "#1565C0" },
        };
        const style = typeColors[row?.chargeType] || {
          bg: "#F5F5F5",
          color: "#616161",
        };

        return (
          <Chip
            label={
              row?.chargeType === "Cash on Delivery" ? "COD" : row?.chargeType
            }
            size="small"
            sx={{
              backgroundColor: style.bg,
              color: style.color,
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        );
      },
      grow: 1,
    },
    {
      name: "Amount",
      selector: (row) => row?.charge || 0,
      cell: (row) =>
        row?.charge ? `Rs. ${Math.abs(row.charge).toFixed(2)}` : "Rs. 0.00",
      sortable: true,
      right: true,
      grow: 1,
    },
    {
      name: "Date",
      selector: (row) => row?.createdAt || "",
      cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
      sortable: true,
      grow: 1,
    },
    {
      name: "Delivery Date",
      selector: (row) => row?.actualDeliveryTime || "",
      cell: (row) => (
        <AppTableDateTime date={row?.actualDeliveryTime} dateOnly />
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: "Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => (
        <Chip
          label={row?.paymentStatus?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.paymentStatus).backgroundColor,
            color: getStatusStyle(row?.paymentStatus).textColor,
            fontWeight: "bold",
            fontSize: "0.3rem",
            height: "24px",
            borderRadius: "12px",
            border: "none",
          }}
        />
      ),
      grow: 1,
    },
  ];

  useEffect(() => {
    const params = {
      userId,
      limit: perPage,
      page: currentPage,
    };

    // Only add filters if they're not "all"
    if (paymentStatus && paymentStatus !== "all") {
      params.paymentStatus = paymentStatus;
    }

    if (chargeType && chargeType !== "all") {
      params.chargeType = chargeType;
    }

    if (shipmentActive && shipmentActive !== "all") {
      params.shipmentActive = shipmentActive;
    }

    fetchPendingUserReportDetail(params);
  }, [
    currentPage,
    fetchPendingUserReportDetail,
    paymentStatus,
    perPage,
    userId,
    chargeType,
    shipmentActive,
  ]);

  // Dynamic title based on charge type
  const getTitle = () => {
    if (chargeType === "Charge") return "Charge Report Detail";
    if (chargeType === "Cash on Delivery") return "COD Report Detail";
    return "Pending Payment Report Detail";
  };

  const getEmptyMessage = () => {
    const typeText =
      chargeType === "Charge"
        ? "charge"
        : chargeType === "Cash on Delivery"
        ? "COD"
        : "";

    const statusText =
      paymentStatus !== "all" ? ` with status "${paymentStatus}"` : "";

    return `No ${typeText} payments found for this user${statusText}.`;
  };

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error />
      <BreadcrumbLayout.Paper>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {getTitle()}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", color: "primary.main" }}
              >
                {detailRes?.data?.[0]?.responsibleUser?.name || "Loading..."}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {chargeType === "Charge"
                  ? "Charge Payments"
                  : chargeType === "Cash on Delivery"
                  ? "Cash on Delivery Payments"
                  : "All Payment Types"}
                {paymentStatus !== "all" && ` • ${paymentStatus}`}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <BaseSelect
                label="Payment Type"
                value={chargeType}
                onChange={(e) => setChargeType(e.target.value)}
                options={[
                  { label: "All Types", value: "all" },
                  { label: "Charge", value: "Charge" },
                  { label: "COD", value: "Cash on Delivery" },
                  { label: "Discount", value: "Discount" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 180 }}
              />

              <BaseSelect
                label="Payment Status"
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                options={[
                  { label: "All Statuses", value: "all" },
                  { label: "Not Paid", value: "Not Paid" },
                  { label: "Paid", value: "Paid" },
                  { label: "Collected", value: "Collected" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 180 }}
              />

              <BaseSelect
  label="Shipment Status"
  value={shipmentActive}
  onChange={(e) => setShipmentActive(e.target.value)}
  options={[
    { label: "Active", value: "true" },
    { label: "Inactive", value: "false" },
    { label: "All", value: "all" },
  ]}
  getOptionLabel={(opt) => opt.label}
  getOptionValue={(opt) => opt.value}
  size="small"
  sx={{ minWidth: 180 }}
/>

              <ExportUserCODReportExcel
                userId={userId}
                chargeType={chargeType}
                paymentStatus={paymentStatus}
              />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailRes?.data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {getEmptyMessage()}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Try changing the filters above to see more results
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {detailRes?.data?.length || 0} of{" "}
                  {detailRes?.totalItems || 0} records
                </Typography>
              </Box>
              <AppTable
                columns={columns}
                data={detailRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={detailRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            </>
          )}
        </Box>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default DeliveryUserReportDetail;
